import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { MyTask } from '@app/models/my-task';
import { CacheService } from '@app/services/cache.service';
import { PlannerBucket, PlannerPlan, PlannerTask, PlannerTaskDetails } from '@microsoft/microsoft-graph-types';
import { concat, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PlannerService {

  constructor(private http: HttpClient, private cache: CacheService, private httpHelper: HttpHelper) { }

  public useCache = true;
  private readonly cacheKeyPrefix = 'PlannerService';

  getMyPlannerTasks(top: number): Observable<Array<PlannerTask>> {

    const cacheKey = `${this.cacheKeyPrefix}-getMyPlannerTasks-${top}`;

    const fetchOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/me/planner/tasks?$top=${top}`)
      .pipe(
        map(response => {
          console.log(`fetched result: getMyPlannerTasks `, response);
          this.useCache && this.cache.set(cacheKey, response.value);
          return response.value;
        }));

    if (this.useCache) {
      const cachedData = this.cache.get<Array<PlannerTask>>(cacheKey);
      if (cachedData) {
        return concat(of(cachedData), fetchOperation);
      }
    }
    return fetchOperation;
  }

  getPlannerTaskDetails(id: string): Observable<PlannerTaskDetails> {

    const cacheKey = `${this.cacheKeyPrefix}-getPlannerTaskDetails-${id}`;

    const fetchOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/planner/tasks/${id}/details`)
      .pipe(
        map(response => {
          console.log(`fetched result: getPlannerTaskDetails `);
          console.log(response);
          this.useCache && this.cache.set(cacheKey, response);
          return response;
        }));

    if (this.useCache) {
      const cachedData = this.cache.get<PlannerTaskDetails>(cacheKey);
      if (cachedData) {
        return concat(of(cachedData), fetchOperation);
      }
    }
    return fetchOperation;
  }

  getPlannerPlan(id: string): Observable<PlannerPlan> {

    const cacheKey = `${this.cacheKeyPrefix}-getPlannerPlan-${id}`;

    const fetchOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/planner/plans/${id}`)
      .pipe(
        map(response => {
          console.log(`fetched result: getPlannerPlan `);
          console.log(response);
          this.useCache && this.cache.set(cacheKey, response);
          return response;
        }));

    if (this.useCache) {
      const cachedData = this.cache.get<PlannerPlan>(cacheKey);
      if (cachedData) {
        return concat(of(cachedData), fetchOperation);
      }
    }
    return fetchOperation;
  }

  getPlannerBucket(id: string): Observable<PlannerBucket> {

    const cacheKey = `${this.cacheKeyPrefix}-getPlannerBucket-${id}`;

    const fetchOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/planner/buckets/${id}`)
      .pipe(
        map(response => {
          console.log(`fetched result: getPlannerBucket `);
          console.log(response);
          this.useCache && this.cache.set(cacheKey, response);
          return response;
        }));

    if (this.useCache) {
      const cachedData = this.cache.get<PlannerBucket>(cacheKey);
      if (cachedData) {
        return concat(of(cachedData), fetchOperation);
      }
    }
    return fetchOperation;
  }

  completePlannerTask(task: MyTask): Observable<PlannerTask> {

    const completed = {
      'percentComplete': 100
    };

    const headers = new HttpHeaders().append('If-Match', task.Etag);
    return this.http.patch<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/planner/tasks/${task.Id}`, completed, {
      headers
    })
      .pipe(
        tap(response => {
          console.log(`completed planner task: `);
          console.log(response);
        }),
        catchError(this.httpHelper.handleError({ operation: 'completePlannerTask', result: {}, rethrow: true })));
  }
}
